import {
  App,
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  financeApi,
  type Account,
  type BankAccount,
  type BankAccountInput,
} from '@/api/finance';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const PKR = (n: number | string) =>
  Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

interface FormShape {
  accountName: string;
  bankName?: string;
  accountNumber?: string;
  branch?: string;
  cash: boolean;
  glAccountId: number;
  openingBalance: number;
  openingBalanceDate: Dayjs;
}

export function FinanceBankAccountsPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('FINANCE:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('FINANCE:EDIT'));

  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'bank-accounts'],
    queryFn: financeApi.listBankAccounts,
  });

  const { data: accounts } = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: financeApi.listAccounts,
    staleTime: 60_000,
  });

  const upsert = useMutation({
    mutationFn: (values: FormShape) => {
      const body: BankAccountInput = {
        accountName: values.accountName.trim(),
        bankName: values.bankName?.trim() || undefined,
        accountNumber: values.accountNumber?.trim() || undefined,
        branch: values.branch?.trim() || undefined,
        cash: values.cash,
        glAccountId: values.glAccountId,
        openingBalance: values.openingBalance.toFixed(2),
        openingBalanceDate: values.openingBalanceDate.format('YYYY-MM-DD'),
      };
      return editing
        ? financeApi.updateBankAccount(editing.id, body)
        : financeApi.createBankAccount(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Bank account updated' : 'Bank account created');
      qc.invalidateQueries({ queryKey: ['finance', 'bank-accounts'] });
      setEditing(null);
      setCreating(false);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) =>
      financeApi.toggleBankAccount(id, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['finance', 'bank-accounts'] }),
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    setCreating(true);
    form.resetFields();
    form.setFieldsValue({
      cash: false,
      openingBalance: 0,
      openingBalanceDate: dayjs(),
    });
  };
  const openEdit = (row: BankAccount) => {
    setCreating(false);
    setEditing(row);
    form.setFieldsValue({
      accountName: row.accountName,
      bankName: row.bankName ?? '',
      accountNumber: row.accountNumber ?? '',
      branch: row.branch ?? '',
      cash: row.cash,
      glAccountId: row.glAccountId,
      openingBalance: Number(row.openingBalance),
      openingBalanceDate: dayjs(row.openingBalanceDate),
    });
  };

  const open = creating || editing != null;
  const assetAccounts = (accounts ?? []).filter(
    (a: Account) => a.accountType === 'ASSET' && a.postingAccount && a.active,
  );

  const columns: TableProps<BankAccount>['columns'] = [
    {
      title: 'Account',
      dataIndex: 'accountName',
      render: (n: string, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text strong>{n}</Typography.Text>
          {!row.cash && row.bankName && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.bankName}{row.branch ? ` · ${row.branch}` : ''}
            </Typography.Text>
          )}
          {row.cash && <Tag color="success">Cash</Tag>}
        </Space>
      ),
    },
    {
      title: 'Account #',
      dataIndex: 'accountNumber',
      width: 180,
      render: (n: string | null) =>
        n ? <Typography.Text code>{n}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'GL link',
      dataIndex: 'glAccountName',
      width: 220,
      render: (n: string | null) => n ?? '—',
    },
    {
      title: 'Opening',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space orientation="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <span>PKR {PKR(row.openingBalance)}</span>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {dayjs(row.openingBalanceDate).format('D MMM YYYY')}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Current balance',
      dataIndex: 'currentBalance',
      width: 160,
      align: 'right',
      render: (a: string) => (
        <Typography.Text strong style={{ color: Number(a) < 0 ? '#dc2626' : undefined }}>
          PKR {PKR(a)}
        </Typography.Text>
      ),
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 90,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Finance"
        title="Bank & cash accounts"
        description="Each receipt or payment posts to one of these. Exactly one cash account is allowed."
        count={data ? { value: data.length, label: 'accounts' } : undefined}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New account
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit ${editing.accountName}` : 'New bank/cash account'}
        open={open}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        {open && (
          <Form form={form} layout="vertical" onFinish={(values) => upsert.mutate(values)}>
            <Form.Item label="Account name" name="accountName" rules={[{ required: true, max: 200 }]}>
              <Input placeholder="e.g. HBL Current Account" autoFocus />
            </Form.Item>
            <Form.Item label="Cash account" name="cash" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item label="Bank name" name="bankName" rules={[{ max: 100 }]}>
              <Input placeholder="e.g. HBL" />
            </Form.Item>
            <Form.Item label="Account number" name="accountNumber" rules={[{ max: 30 }]}>
              <Input />
            </Form.Item>
            <Form.Item label="Branch" name="branch" rules={[{ max: 100 }]}>
              <Input />
            </Form.Item>
            <Form.Item
              label="GL account"
              name="glAccountId"
              rules={[{ required: true }]}
              tooltip="The asset GL account this bank/cash account maps to."
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={assetAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.code} · ${a.name}`,
                }))}
                placeholder="Pick an ASSET posting account"
              />
            </Form.Item>
            <Form.Item label="Opening balance" name="openingBalance" rules={[{ required: true, type: 'number' }]}>
              <InputNumber style={{ width: '100%' }} step={1000} />
            </Form.Item>
            <Form.Item label="Opening balance date" name="openingBalanceDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
}
